import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LineChart as LineChartIcon } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

const seriesLabels = { revenue: 'Total', basic_revenue: 'Basic', pro_revenue: 'Pro' };

// Hoisted to module scope so these object literals aren't re-created on every
// render (stable references for recharts props).
const CHART_MARGIN = { top: 10, right: 20, left: 0, bottom: 0 };
const AXIS_TICK = { fontSize: 10, fill: '#00aa00' };
const TOOLTIP_STYLE = { background: '#000', border: '1px solid #00ff00', color: '#00ff00', fontSize: 12 };
const LEGEND_STYLE = { fontSize: 12 };
const CHART_MIN_HEIGHT = { minHeight: 240 };
const LEGEND_LABEL_STYLE = { color: '#00cc00' };

export const RevenueChart = ({ revenueSeries, revenueRange, setRevenueRange }) => (
  <Card className="card-tech p-6" data-testid="revenue-chart-card">
    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
      <div>
        <h3 className="text-xl font-bold text-green-400">
          <LineChartIcon className="w-5 h-5 inline mr-2" /> Revenue Trend
        </h3>
        <p className="text-green-500/70 text-sm">
          Daily revenue over the last <span className="text-green-400 font-bold">{revenueRange}</span> days,
          split by <span className="text-green-400">Basic</span> vs <span className="text-emerald-300">Pro</span> tier.
        </p>
      </div>
      <div className="flex gap-2">
        {[7, 30, 90].map(d => (
          <Button
            key={d}
            onClick={() => setRevenueRange(d)}
            className={`text-xs px-3 py-1 ${revenueRange === d ? 'btn-primary' : 'btn-secondary'}`}
            data-testid={`revenue-range-${d}`}
          >
            {d}d
          </Button>
        ))}
      </div>
    </div>
    <div className="h-64 w-full" data-testid="revenue-chart" style={CHART_MIN_HEIGHT}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={revenueSeries} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#003300" />
          <XAxis
            dataKey="date"
            stroke="#00aa00"
            tick={AXIS_TICK}
            tickFormatter={d => d?.slice(5)}
          />
          <YAxis
            stroke="#00aa00"
            tick={AXIS_TICK}
            tickFormatter={v => `$${v}`}
            width={50}
          />
          <Tooltip
            contentStyle={TOOLTIP_STYLE}
            formatter={(value, name) => [`$${Number(value).toFixed(2)}`, seriesLabels[name] || name]}
          />
          <Legend
            wrapperStyle={LEGEND_STYLE}
            formatter={(value) => <span style={LEGEND_LABEL_STYLE}>{seriesLabels[value] || value}</span>}
          />
          <Line type="monotone" dataKey="revenue" name="revenue" stroke="#00ff00" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="basic_revenue" name="basic_revenue" stroke="#facc15" strokeWidth={2} dot={false} strokeDasharray="4 2" />
          <Line type="monotone" dataKey="pro_revenue" name="pro_revenue" stroke="#5eead4" strokeWidth={2} dot={false} strokeDasharray="4 2" />
        </LineChart>
      </ResponsiveContainer>
    </div>
    {revenueSeries.length === 0 && (
      <p className="text-center text-green-500/50 py-4 text-sm">No data yet — chart will populate as sales come in.</p>
    )}
  </Card>
);
